<script lang="ts" setup>
import { ref, reactive, watch, onUnmounted, computed, onMounted, nextTick } from 'vue';
import { useLocalization } from '@/plugins/localization';
import { useEvents } from '@/plugins/events';
import { useStore } from 'vuex';
import { useAlerts } from '@/plugins/alerts';
import Pager from '@/helpers/Pager';
import { Form } from '@/helpers/Form';
import { scrollRedirect } from '@/components/common/dynamic-grid/helpers/scrollRedirect';
import IdeoModal from '@/components/ideo/modal/IdeoModal.vue';
import axios from 'axios';

const { $t, $i18n } = useLocalization();
const store = useStore();
const { $events } = useEvents();
const { $alert } = useAlerts();

interface PermissionCheckResponse {
    canSee: boolean;
    baseUrl: string;
}

const emit = defineEmits(["document-selected", "hide"]);

const props = defineProps({
  "licence": null
});

const modal = ref<IdeoModal | null>(null);
const userId = ref<string>('');
const isExpiredOnly = ref<boolean>(false);
const title = ref<string>('');
const hiddenFilters = ref<string[]>([]);
const isModalVisible = ref<boolean>(false);
const permissionCache = new Map<string, PermissionCheckResponse>();

const pager = reactive(new Pager(1, 10, 'Id', 'desc')) as Pager;
const filter = reactive(Form.create({})) as any;
const filterBuilder = reactive<Record<string, unknown>>({});

const currentLicence = ref<string>(props.licence);
const endpoint = computed(() => `${currentLicence.value}/task-report-details`);
const filtersDone = computed(() => store.state.common.grid.filtersInitialized);

const extraParams = computed(() => ({
    userId: userId.value,
    expiredOnly: isExpiredOnly.value
}));

const checkDocumentPermission = async (publicId: string): Promise<PermissionCheckResponse> =>
{
    // Check cache first
    if (permissionCache.has(publicId))
    {
        return permissionCache.get(publicId) as PermissionCheckResponse;
    }

    try
    {
        const response = await axios.get<PermissionCheckResponse>(`documents/${publicId}/can-see`);

        permissionCache.set(publicId, response.data);

        return response.data;
    }
    catch (error)
    {
        // If API call fails, assume no permission
        const noPermission: PermissionCheckResponse = { canSee: false, baseUrl: '' };

        permissionCache.set(publicId, noPermission);

        return noPermission;
    }
};

// TODO: Rewrite filters
const overwriteFilter = (event: { key: string; value: any; period: boolean }) =>
{
    if (event.period)
    {
        filterBuilder[`${event.key}.FromUtc`] = event.value;
        filterBuilder[`${event.key}.ToUtc`] = event.value;
        filterBuilder[`${event.key}.DaysBeforeNow`] = event.value;
        filterBuilder[`${event.key}.DaysAfterNow`] = event.value;
    }
    else filterBuilder[event.key] = event.value;
};

const onRowClick = async (item: any) =>
{
    const permission = await checkDocumentPermission(item.result.publicId);

    if (permission.canSee)
    {
        emit('document-selected', item.result.publicId, currentLicence.value);
        modal.value?.hide();
    }
    else
    {
        $alert.warning('Nie posiadzasz uprawnień do szczegółów tego dokumentu');
    }
};

const onScrollClick = async (item: any) =>
{
    const permission = await checkDocumentPermission(item.result.publicId);

    if (permission.canSee)
    {
        scrollRedirect({
            name: 'module-form-details',
            params: { licence: item.result.licence, publicId: item.result.publicId },
        });
    }
    else
    {
        $alert.warning('Nie posiadzasz uprawnień do szczegółów tego dokumentu');
    }
};

watch(filtersDone, (value) =>
{
    if (value)
    {
        filter.setInitialValues(filterBuilder);
        filter.withData(filterBuilder);
    }
});

watch(isModalVisible, async (visible) =>
{
    if (visible && userId.value)
    {
        store.commit('common/grid/SET_INITIALIZATION', false);

        await nextTick();

        filter.withData({ ...filterBuilder });
    }
});

const showModal = async () =>
{
    isModalVisible.value = true;
    await nextTick();
    modal.value?.show();
};

const hideModal = () =>
{
    isModalVisible.value = false;
    userId.value = '';
    isExpiredOnly.value = false;
    title.value = '';
};

onMounted(() =>
{
    $events.$on('task-report-modal', async (row: any, expiredOnly: boolean) =>
    {
        userId.value = row.user.key || '';
        isExpiredOnly.value = expiredOnly;
        title.value = $t('[[[Raport zadań]]]') + (row.user.name ? ` - ${row.user.name}` : '');

        // Use licence from backend response
        if (row.licence)
        {
            currentLicence.value = row.licence;
        }

        await showModal();
    });
});

onUnmounted(() =>
{
    store.commit('common/grid/SET_INITIALIZATION', false);

    $events.$off('task-report-modal');
});

defineExpose({
    showModal,
});
</script>

<template>
    <IdeoModal ref="modal" centered size="xxl" :title="title" @hidden="hideModal">
        <template #default>
            <DynamicGrid
                v-if="isModalVisible && userId"
                :key="`task-report-${userId}-${isExpiredOnly}`"
                :pager="pager"
                :filter="filter"
                :endpoint="endpoint"
                :view-state-key="endpoint"
                :row-click="onRowClick"
                :scroll-click="onScrollClick"
                :persist-state="false"
                :extra-params="extraParams"
            >
                <template #toolbar>
                    <IdeoPanel :column="false" :stretch="false" :start="desktop">
                        <template #default>
                            <NewFiltersWrapper :filter="filter" :url="currentLicence" :pager="pager" :view-state-key="endpoint">
                                <DynamicFilters
                                    :request-url="currentLicence"
                                    :filter="filter"
                                    :hidden-filters="hiddenFilters"
                                    :view-state-key="endpoint"
                                    @filter-update="overwriteFilter"
                                />
                            </NewFiltersWrapper>
                        </template>
                    </IdeoPanel>
                </template>
                <template #actions="{ item }">
                    <RowActions>
                        <IdeoDropdownItemButton @click="onRowClick(item)">
                            <i class="fas fa-square-list"></i>
                            <span>{{ $t('[[[Szczegóły]]]') }}</span>
                        </IdeoDropdownItemButton>
                    </RowActions>
                </template>
            </DynamicGrid>
        </template>
        <template #modal-footer="{ cancel }">
            <IdeoButton variant="secondary" @click="cancel">
                {{ $t('[[[Anuluj]]]') }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>
